import dayjs from 'dayjs';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { isEmpty } from 'utils/functions';

export { isEmpty } from 'utils/functions';

export const isValidName = (name: string): boolean => {
  const tester = /[;<>≤≥`~§±!@#$%^&*_=+≠{}|"?÷]/;
  return !isEmpty(name) && !tester.test(name);
};

export const isValidEmail = (email: string | null, options = { trim: false }): boolean => {
  if (!email) {
    return false;
  }
  const s = options.trim ? email.trim() : email;

  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(s);
};

export const isValidPassword = (password: string): boolean => {
  const re = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/;
  return re.test(password);
};

// The set of every supported country calling code (e.g. '1', '44', '61'), used
// to detect a "bare dial code" value. Built once at module load.
const DIAL_CODES = new Set<string>(getCountries().map(country => getCountryCallingCode(country)));

/**
 * True when the value is only a country dial code with no subscriber number
 * (e.g. '+1', '+44', '+61').
 *
 * The `react-international-phone` PhoneInput never sits truly empty: when a field
 * is untouched it holds the dial code of its `defaultCountry` (the gym's country).
 * For a US gym that is '+1', for a UK gym '+44', etc. A bare dial code therefore
 * means "the user left this field empty", not "the user entered an invalid number".
 */
export const isBareDialCode = (phone: string): boolean => {
  const trimmed = phone.trim();
  if (!trimmed.startsWith('+')) return false;
  return DIAL_CODES.has(trimmed.slice(1).replace(/\D/g, ''));
};

export const isValidPhone = (phone: string): boolean => {
  // A bare dial code (e.g. '+1' for a US gym, '+44' for a UK gym) is an untouched
  // PhoneInput, i.e. an empty value. Treat it as valid so optional phone fields do
  // not block save on non-US gyms. See isBareDialCode.
  if (isBareDialCode(phone)) {
    return true;
  }
  const phoneNumber = parsePhoneNumberFromString(phone);
  return phoneNumber ? phoneNumber.isValid() : false;
};

export const isValidDate = (phone: string): boolean => {
  const re = /^(0[1-9]|1[012])[/](0[1-9]|[12][0-9]|3[01])[/](19|20)\d\d$/;
  return re.test(phone);
};

export const isValidSenderName = (senderName: string | null): boolean => {
  const re = /^[a-zA-Z0-9\s.'&-]{1,64}$/;
  if (!senderName) {
    return false;
  }
  return re.test(senderName);
};

export const senderNameValidator = (value: string | null): string | null => {
  if (isEmpty(value)) {
    return 'Sender name is required.';
  }
  if (!isValidSenderName(value)) {
    return 'Please provide a valid sender name (1-64 characters). Letters, numbers, spaces, apostrophes, ampersands, periods, and hyphens are allowed.';
  }
  return null;
};

export const replyToEmailValidator = (replyToEmail: string | null): string | null => {
  if (isEmpty(replyToEmail)) {
    return "'Reply to' email is required.";
  }
  if (!isValidEmail(replyToEmail)) {
    return "Please provide a valid 'Reply To' value.";
  }
  return null;
};

export const emailValidator = (value: string | null): string | null => {
  if (isEmpty(value)) return 'Email field is required.';
  if (!isValidEmail(value)) return 'Please provide a valid email.';
  return null;
};

export const passwordValidator = (value: string): string | null => {
  if (isEmpty(value)) return 'Password field is required.';
  if (!isValidPassword(value))
    return 'Length should be more than 8 characters. Must contain at least one number, one uppercase, and one lowercase letter.';
  return null;
};

export const newPasswordValidator =
  (currentPassword: string) =>
  (value: string): string | null => {
    if (value === currentPassword) return 'New password matches current, please change new password';
    if (value === undefined || value.length === 0) return '';
    if (!isValidPassword(value))
      return 'Length should be more than 8 characters. Must contain at least one number, one uppercase, and one lowercase letter.';
    return null;
  };

export const rePasswordValidator =
  (password: string) =>
  (value: string): string | null => {
    if (password !== value) return "Password doesn't match.";
    return passwordValidator(value);
  };

export const phoneValidator = (value: string): string | null => {
  if (isEmpty(value)) return 'Phone number is required.';
  return isValidPhone(value) ? null : 'Please provide a valid phone number.';
};

/**
 * Strict phone validator for flows where a phone number is actually required
 * (Sales Portal checkout, phone verification).
 *
 * The shared `phoneValidator` treats a bare dial code (e.g. '+1', '+44') as valid
 * because it represents an untouched, empty PhoneInput — the right behavior for
 * optional fields. When the phone is required, a bare dial code means the user
 * entered nothing, so it must be rejected regardless of the gym's country.
 */
export const requiredPhoneValidator = (value: string): string | null => {
  if (isEmpty(value) || isBareDialCode(value)) return 'Phone number is required.';
  return isValidPhone(value) ? null : 'Please provide a valid phone number.';
};

export const nameValidator = (value: string): string | null => {
  if (isEmpty(value)) return 'Name field is required.';
  return isValidName(value) ? null : 'Please provide a valid name.';
};

export const dateValidator = (value: string): string | null => {
  if (isEmpty(value)) return 'Date field is required.';
  return isValidDate(value) ? null : 'Please provide a valid date.';
};

export const optionalDateFormatValidator = (
  value: Date | null | undefined,
  dateFormatLabel: string,
): string | null => {
  if (value == null) return null;

  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return `Please enter a date matching the format ${dateFormatLabel}`;
  }

  const d = dayjs(value);
  if (!d.isValid()) return `Please enter a date matching the format ${dateFormatLabel}`;

  return null;
};

export const optionalPastDateValidator = (value: Date | null | undefined, label: string): string | null => {
  if (value == null) return null;
  return dayjs(value).isAfter(dayjs(), 'day') ? `${label} cannot be in the future` : null;
};

export const optionalMinYearValidator = (value: Date | null | undefined, minYear = 1900): string | null => {
  if (value == null) return null;
  return dayjs(value).year() < minYear ? `Please enter a valid year (${minYear} or later)` : null;
};
